import React from 'react';

import classNames from 'classnames';

import {
	TypographyBodySize,
	type TypographyBodyProps,
	TypographyColor,
	type TypographyProps,
	type TypographyVariant
} from './types';

import styles from './typography.module.scss';

const VARIANT_CLASS: Record<TypographyVariant, string> = {
	h1: styles.h1,
	h2: styles.h2,
	h3: styles.h3,
	h4: styles.h4,
	h5: styles.h5,
	bodyMedium: styles.bodyMedium,
	bodySmall: styles.bodySmall,
	label: styles.label,
	helper: styles.helper,
	strong: styles.strong
};

const VARIANT_TAG: Record<TypographyVariant, React.ElementType> = {
	h1: 'h1',
	h2: 'h2',
	h3: 'h3',
	h4: 'h4',
	h5: 'h5',
	bodyMedium: 'p',
	bodySmall: 'p',
	label: 'span',
	helper: 'p',
	strong: 'strong'
};

const COLOR_CLASS: Record<TypographyColor, string> = {
	[TypographyColor.Regular]: styles.colorRegular,
	[TypographyColor.Secondary]: styles.colorSecondary,
	[TypographyColor.Subtle]: styles.colorSubtle,
	[TypographyColor.Disabled]: styles.colorDisabled,
	[TypographyColor.Inverse]: styles.colorInverse,
	[TypographyColor.Brand]: styles.colorBrand,
	[TypographyColor.Danger]: styles.colorDanger,
	[TypographyColor.Warning]: styles.colorWarning,
	[TypographyColor.Success]: styles.colorSuccess
};

const BODY_SIZE_VARIANT: Record<TypographyBodySize, TypographyVariant> = {
	[TypographyBodySize.Medium]: 'bodyMedium',
	[TypographyBodySize.Small]: 'bodySmall'
};

type BaseProps = TypographyProps & {
	variant: TypographyVariant;
};

const Typography: React.FC<BaseProps> = ({
	variant,
	tag,
	color = TypographyColor.Regular,
	className,
	children,
	...rest
}) => {
	const Element = tag ?? VARIANT_TAG[variant];

	return (
		<Element
			className={classNames(styles.typography, VARIANT_CLASS[variant], COLOR_CLASS[color], className)}
			{...rest}
		>
			{children}
		</Element>
	);
};

export const TypographyH1: React.FC<TypographyProps> = props => <Typography variant="h1" {...props} />;

export const TypographyH2: React.FC<TypographyProps> = props => <Typography variant="h2" {...props} />;

export const TypographyH3: React.FC<TypographyProps> = props => <Typography variant="h3" {...props} />;

export const TypographyH4: React.FC<TypographyProps> = props => <Typography variant="h4" {...props} />;

export const TypographyH5: React.FC<TypographyProps> = props => <Typography variant="h5" {...props} />;

export const TypographyP: React.FC<TypographyBodyProps> = ({ size = TypographyBodySize.Medium, ...props }) => (
	<Typography variant={BODY_SIZE_VARIANT[size]} {...props} />
);

export const TypographyLabel: React.FC<TypographyProps> = props => <Typography variant="label" {...props} />;

export const TypographyHelper: React.FC<TypographyProps> = props => <Typography variant="helper" {...props} />;

export const TypographyStrong: React.FC<TypographyProps> = props => <Typography variant="strong" {...props} />;
